import { useEffect, useState } from "react";
import { Bell, BellOff, Check, Send, Smartphone } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import {
  currentSubscription,
  disablePush,
  enablePush,
  needsHomeScreen,
  permissionState,
  pushSupported,
} from "@/lib/push";

const LABELS = {
  calls: { title: "Booked calls", desc: "When a coaching call is booked or cancelled." },
  deals: { title: "Deals", desc: "New submissions and status changes on deals." },
  leads: { title: "Leads", desc: "When a new lead lands in the pipeline." },
  coach: { title: "Coach replies", desc: "Comments and feedback on your deals." },
  support: { title: "Support", desc: "New tickets and replies in the support inbox." },
  announcements: { title: "Announcements", desc: "Coach announcements posted to the dashboard." },
};

/** Notifications card for Settings — device toggle + per-event preferences. */
export const PushSettings = () => {
  const { user } = useAuth();
  const [config, setConfig] = useState(null);
  const [subscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [testing, setTesting] = useState(false);

  const supported = pushSupported();
  const homeScreen = needsHomeScreen();

  useEffect(() => {
    api.get("/push/config").then(({ data }) => setConfig(data)).catch(() => setConfig(null));
    if (supported) currentSubscription().then((s) => setSubscribed(!!s));
  }, [supported]);

  const toggleDevice = async () => {
    setBusy(true);
    try {
      if (subscribed) {
        const { devices } = await disablePush();
        setSubscribed(false);
        setConfig((c) => (c ? { ...c, devices } : c));
        toast.success("Alerts off for this device");
      } else {
        const { devices } = await enablePush();
        setSubscribed(true);
        setConfig((c) => (c ? { ...c, devices } : c));
        toast.success("Alerts on for this device");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  const sendTest = async () => {
    setTesting(true);
    try {
      await api.post("/push/test");
      toast.success("Test alert sent");
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setTesting(false);
    }
  };

  const togglePref = async (key) => {
    const next = { ...config.prefs, [key]: !config.prefs[key] };
    setConfig({ ...config, prefs: next });
    try {
      const { data } = await api.put("/push/prefs", { prefs: { [key]: next[key] } });
      setConfig((c) => ({ ...c, prefs: data.prefs }));
    } catch (e) {
      setConfig((c) => ({ ...c, prefs: { ...c.prefs, [key]: !next[key] } }));
      toast.error("Could not save that preference");
    }
  };

  const categories = user?.is_admin
    ? ["calls", "deals", "leads", "support", "coach"]
    : ["calls", "deals", "coach", "support", "announcements"];

  return (
    <div className="surface-card p-6 mb-6" data-testid="push-settings-card">
      <div className="flex items-center gap-2 mb-1">
        <Bell size={16} style={{ color: "var(--lime)" }} />
        <div className="font-mono text-xs uppercase tracking-wider" style={{ color: "var(--lime)" }}>
          Phone alerts
        </div>
      </div>
      <p className="text-xs mb-5" style={{ color: "var(--text-muted)" }}>
        Push notifications land on your phone or desktop even when the app is closed.
      </p>

      {!supported ? (
        <div className="text-sm" style={{ color: "var(--text-muted)" }} data-testid="push-unsupported">
          This browser doesn&apos;t support push notifications. Try Chrome, Edge, Firefox, or Safari 16.4+.
        </div>
      ) : (
        <>
          {homeScreen && !subscribed && (
            <div
              className="p-4 rounded-2xl mb-5 flex items-start gap-3"
              style={{ background: "var(--surface2)", border: "1px solid var(--border)" }}
              data-testid="push-ios-hint"
            >
              <Smartphone size={16} style={{ color: "var(--lime)" }} className="mt-0.5 shrink-0" />
              <p className="text-xs" style={{ color: "var(--text-muted)" }}>
                On iPhone or iPad: tap the <strong>Share</strong> icon in Safari, choose{" "}
                <strong>Add to Home Screen</strong>, then open the app from your home screen and turn alerts on here.
              </p>
            </div>
          )}

          <div className="flex items-center justify-between gap-4 flex-wrap mb-2">
            <div className="min-w-0">
              <div className="font-medium text-sm">This device</div>
              <div className="text-xs" style={{ color: "var(--text-muted)" }} data-testid="push-device-status">
                {subscribed
                  ? `Alerts are on${config?.devices ? ` · ${config.devices} device${config.devices === 1 ? "" : "s"} registered` : ""}`
                  : permissionState() === "denied"
                    ? "Blocked in browser settings"
                    : "Alerts are off"}
              </div>
            </div>
            <div className="flex items-center gap-2">
              {subscribed && (
                <button
                  onClick={sendTest}
                  disabled={testing}
                  className="btn-ghost text-sm flex items-center gap-2"
                  data-testid="push-send-test-button"
                >
                  {testing ? <span className="spinner" /> : <Send size={14} />} Send test
                </button>
              )}
              <button
                onClick={toggleDevice}
                disabled={busy || (homeScreen && !subscribed)}
                className={subscribed ? "btn-ghost text-sm flex items-center gap-2" : "btn-primary text-sm flex items-center gap-2"}
                data-testid="push-toggle-device-button"
              >
                {busy ? <span className="spinner" /> : subscribed ? <BellOff size={14} /> : <Bell size={14} />}
                {subscribed ? "Turn off" : "Turn on alerts"}
              </button>
            </div>
          </div>

          {config?.prefs && (
            <div className="mt-6">
              <div className="font-mono text-[11px] uppercase tracking-wider mb-3" style={{ color: "var(--text-dim)" }}>
                What to alert me about
              </div>
              <div className="flex flex-col gap-2">
                {categories.map((key) => (
                  <button
                    key={key}
                    onClick={() => togglePref(key)}
                    className="flex items-center gap-3 p-3 rounded-2xl text-left transition"
                    style={{
                      background: config.prefs[key] ? "var(--lime-glow)" : "var(--surface2)",
                      border: config.prefs[key] ? "1px solid var(--lime)" : "1px solid var(--border)",
                    }}
                    data-testid={`push-pref-${key}`}
                  >
                    <div
                      className="w-5 h-5 rounded-md flex items-center justify-center shrink-0"
                      style={{
                        background: config.prefs[key] ? "var(--lime)" : "transparent",
                        border: config.prefs[key] ? "none" : "1px solid var(--border)",
                      }}
                    >
                      {config.prefs[key] && <Check size={13} style={{ color: "var(--bg)" }} />}
                    </div>
                    <div className="min-w-0">
                      <div className="text-sm font-medium">{LABELS[key].title}</div>
                      <div className="text-xs" style={{ color: "var(--text-muted)" }}>{LABELS[key].desc}</div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default PushSettings;
